// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { AdvancedInfoTip, AdvancedInfoTipAjvSchema, createPortStateFromInfoTipEvent, InfoTipItem, InfoTipLayout, renderInfoTip } from "@Primitive/InfoTips";
import type { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';
import { StateStatus, WebThingPortType } from '@API/WebNodeTypes';
import { WebThing, WebThingPort } from '@API/WebThing';
import { IWidgetDescription, IWidgetSettingsProps, WidgetDescriptionToPlegmaThing } from '@UI/Widgets/WidgetStructs';
import { SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { SettingsList } from '@Primitive/SettingsList';
import { isDefined, isFunction, IsNullOrEmpty } from '@Utils/Micro';
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';

// =================================================================================================
const enum PortKeys {
    Data = "data",
    OnDataChange = "on_data_change"
}
// -------------------------------------------------------------------------------------------------
interface InfoTipCoreData {
    Layout: InfoTipLayout;
    BigIconSize: number;
    FontSize: number;
    FontColor: string;
    BackgroundColor: string;
    OpenIcon?: string;
    CloseIcon?: string;
    AlignLeft?: boolean;
}
// -------------------------------------------------------------------------------------------------
interface InfoTipCoreState {
    advancedInfotip: AdvancedInfoTip;
}
//--------------------------------------------------------------------------------------------------
class InfoTipCore extends React.Component<WidgetsThingsStructs.PartialIWidgetThingsProps<InfoTipCoreData>, InfoTipCoreState> {
    // ---------------------------------------------------------------------------------------------
    constructor(props: WidgetsThingsStructs.PartialIWidgetThingsProps<InfoTipCoreData>) {
        super(props);
        this.state = {
            advancedInfotip: null
        };
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidMount() {
        this.getAndUpdateData();
    }
    // ---------------------------------------------------------------------------------------------------
    componentDidUpdate(prevProps: WidgetsThingsStructs.PartialIWidgetThingsProps<InfoTipCoreData>) {
        if (prevProps.PortVersion != this.props.PortVersion) {
            this.getAndUpdateData();
        }
    }
    // ---------------------------------------------------------------------------------------------
    getAndUpdateData = () => {
        if (_.has(this.props.PortsStates, PortKeys.Data)) {
            let port = this.props.PortsStates[PortKeys.Data];
            if (port.LastStateStatus == StateStatus.Valid) {
                this.setState({ advancedInfotip: port.State });
            }
        }
    }
    // ---------------------------------------------------------------------------------------------
    handlePopupDataChange = (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => {
        this.props.SendPortEvent(PortKeys.OnDataChange, createPortStateFromInfoTipEvent(i, infoTip, iconClicked));
    }
    // ---------------------------------------------------------------------------------------------
    render() {
        const settings = this.props.WidgetData;
        return renderInfoTip(
            "infotipcore",
            this.props.width,
            this.state.advancedInfotip,
            {
                Layout: settings.Layout,
                BigIconSize: settings.BigIconSize,
                FontSize: settings.FontSize,
                FontColor: settings.FontColor,
                groupOpenIcon: settings.OpenIcon,
                groupCloseIcon: settings.CloseIcon,
                BackgroundColor: settings.BackgroundColor,
                BorderRadius: 2,
                groupAlignLeft: settings.AlignLeft,
                cardStyle: { height: "auto", minHeight: this.props.height }
            },
            this.handlePopupDataChange);
    }
}
// =================================================================================================
class InfoTipCoreSettings extends React.PureComponent<IWidgetSettingsProps<InfoTipCoreData>> {
    constructor(props: IWidgetSettingsProps<InfoTipCoreData>) {
        super(props);
    }
    // ------------------------------------------------------------------------------------------------------------
    render() {
        let settings: SettingsGroup<InfoTipCoreData>[] = [
            {
                name: 'Group Settings',
                elements: [
                    {
                        type: SettingsType.Boolean,
                        label: "Align left/right",
                        tooltip: 'Choose if your icon will be aligned left/right',
                        dataPath: "AlignLeft",
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Open Icon",
                        tooltip: 'Choose an icon for opened group.',
                        dataPath: "OpenIcon",
                    },
                    {
                        type: SettingsType.Icon,
                        label: "Close Icon",
                        tooltip: 'Choose an icon for closed group.',
                        dataPath: "CloseIcon",
                    },
                ]
            },
            {
                name: "",
                elements: [


                    {
                        type: SettingsType.DropDown,
                        label: 'Layout',
                        tooltip: "InfoTip's Layout.",
                        dataPath: "Layout",
                        items: [
                            { label: "Default", value: InfoTipLayout.Default },
                            { label: "Big-Icon", value: InfoTipLayout.BigIcon }
                        ]
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Big Icon Size",
                        tooltip: 'If big-icon layout is selected, define the size of it.',
                        dataPath: "BigIconSize",
                        minValue: 0.1,
                        maxValue: 1,
                        stepValue: 0.05,
                        valuePosfix: "* Tile's Width",
                        isHidden: (data: InfoTipCoreData) => data.Layout == InfoTipLayout.Default
                    },
                    {
                        type: SettingsType.Number,
                        label: 'Font Size',
                        tooltip: "InfoTip's font size.",
                        dataPath: "FontSize",
                        oneLine: true
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Font Color',
                        tooltip: "InfoTip's default font color. Can be override from port.",
                        dataPath: "FontColor"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Background Color',
                        tooltip: "InfoTip's default background color. Can be override from port.",
                        dataPath: "BackgroundColor"
                    }
                ]
            }
        ];

        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.props.WidgetData}
            onSaveData={(data) => {
                if (isFunction(this.props.onWidgetDataUpdate))
                    this.props.onWidgetDataUpdate(data);
            }}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
}
// =================================================================================================
export class InfoTipCoreWidget extends IWidgetThingsClass<InfoTipCoreData> {
    // ------------------------------------------------------------------------------------------------------------
    Render(key: string, width: number, height: number): React.ReactNode {
        return <InfoTipCore />;
    }
    // ------------------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <InfoTipCoreSettings
            key={'settings-' + key}
            width={width}
            height={height}
            WidgetData={_.cloneDeep(this.WidgetData)}
            WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
            ThingKey={this.ThingKey}
            NodeKey={this.NodeKey}
            onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
            SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
        />;
    }
    // ------------------------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: InfoTipCoreData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        ports.push(new WebThingPort({
            Name: 'Data',
            PortKey: PortKeys.Data,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            ConfFlags: Plegma.ePortConf.None,
            Description: 'The InfoTip data',
            State: ''
        }, WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data)));

        ports.push(new WebThingPort({
            Name: 'OnDataChange',
            PortKey: PortKeys.OnDataChange,
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: 'Changes in InfoTip from users',
            State: ''
        }, WebThingPortType.Trigger));

        return ports;
    }
    // ------------------------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ------------------------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as InfoTipCoreData),

            // Create plegma thing.
            plegmaThing = WidgetDescriptionToPlegmaThing(this.Description),

            // Create thing's ports and web thing.
            ports = this.createThingPorts(widgetData),
            webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): InfoTipCoreData {
        return {
            Layout: InfoTipLayout.Default,
            BigIconSize: 0.75,
            FontSize: 14,
            FontColor: "white",
            BackgroundColor: 'rgb(180, 180, 180)',
            OpenIcon: 'far fa-angle-down',
            CloseIcon: 'far fa-angle-left',
            AlignLeft: false,
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return InfoTipCoreWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Infotip',
            WidgetTitle: '',
            Key: 'InfoTipCoreWidget',
            Type: 'InfoTipCoreWidget',
            Data: InfoTipCoreWidget.GetDefaultWidgetDataStatic(),
            Description: 'Infotip widget',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/infotip.svg'
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.Data)
            return AdvancedInfoTipAjvSchemaValidator().DataValidateString(value);

        return { valid: true, errors: '' };
    }
    // ------------------------------------------------------------------------------------------------------------
    public GetDataPortKeyStatic(): string { return PortKeys.Data; }
    // ------------------------------------------------------------------------------------------------------------

}
// ------------------------------------------------------------------------------------------------------------
function AdvancedInfoTipAjvSchemaValidator() {
    return JsonSchemaValidator.GetInstance(
        'AdvancedInfoTipAjvSchemaValidator',
        AdvancedInfoTipAjvSchema
    );
}
// ------------------------------------------------------------------------------------------------------------